import { api } from '@api'
import type { CreateSetup } from '@hvf/contracts'
import * as v from 'valibot'

export type imageProperties = Pick<
  CreateSetup,
  | 'image_macro'
  | 'image_pattern'
  | 'image_trend'
  | 'image_trigger'
  | 'image_time_stop'
  | 'image_volume_by_price'
>

export const handleCreateSetup = async (
  setup?: CreateSetup,
  oldSetup?: CreateSetup,
) => {
  if (setup === undefined) {
    return
  }

  const isEditing = Boolean(oldSetup)

  // 1. Clone and sanitize the payload to prevent DB casting errors
  const payload: any = { ...setup }

  if (!isEditing) {
    // biome-ignore lint/performance/noDelete: <explanation>
    delete payload.id
  }

  // Convert empty strings to null for timestamps, numbers, and enums
  for (const key of Object.keys(payload)) {
    if (payload[key] === '') {
      payload[key] = null
    }
  }

  // 'stage' is NOT NULL in the DB but has a default.
  // If we set it to null above, remove the key entirely so Postgres uses its default.
  if (payload.stage === null) {
    payload.stage = undefined
  }

  payload.symbol = payload.symbol?.toUpperCase().trim() || null

  const allImages: { key: keyof imageProperties; path: string }[] = [
    { key: 'image_macro', path: payload.image_macro || '' },
    { key: 'image_pattern', path: payload.image_pattern || '' },
    { key: 'image_trend', path: payload.image_trend || '' },
    { key: 'image_trigger', path: payload.image_trigger || '' },
    { key: 'image_time_stop', path: payload.image_time_stop || '' },
    { key: 'image_volume_by_price', path: payload.image_volume_by_price || '' },
  ]

  const uploadedFiles: { key: keyof imageProperties; path: string }[] = []

  // Delete images that have been replaced
  const images = isEditing
    ? allImages.filter((value) => {
      const uuidSchema = v.pipe(
        v.string(),
        v.minLength(1),
        v.regex(
          /^(https:\/\/s3\.tradingview\.com\/snapshots\/[A-Za-z0-9]\/[A-Za-z0-9]+\.png)$/,
          'The image must be a TradingView URL',
        ),
      )
      return v.safeParse(uuidSchema, value.path).success
    })
    : allImages.filter((value) => {
      const uuidSchema = v.pipe(
        v.string(),
        v.regex(
          /^(https:\/\/s3\.tradingview\.com\/snapshots\/[A-Za-z0-9]\/[A-Za-z0-9]+\.png)$/,
          'The image must be a TradingView URL',
        ),
      )
      return v.safeParse(uuidSchema, value.path).success
    })

  try {
    // Step 1: Download and upload each image
    const uploadPromises = images.map(async (val) => {
      const response = await fetch(val.path)
      if (!response.ok)
        throw new Error(`Failed to download ${val.key} from ${val.path}`)

      const blob = await response.blob()
      const fileExt = val.path.split('.').pop() || 'png'
      const filePath = `${crypto.randomUUID()}.${fileExt}`

      const { error: uploadError } = await api()
        .storage.from('setup-images')
        .upload(filePath, blob, { contentType: `image/${fileExt}` })

      if (uploadError) throw new Error(uploadError.message)

      uploadedFiles.push(val)
      return { key: val.key, url: filePath }
    })

    if (isEditing) {
      const filesToDelete = images
        .filter(({ key }) => Boolean(oldSetup![key]))
        .map(({ key }) => oldSetup![key]!)
      if (filesToDelete.length) {
        await api().storage.from('setup-images').remove(filesToDelete)
      }
    }

    const uploadedImages = (await Promise.all(uploadPromises)).filter(Boolean) as {
      key: keyof imageProperties
      url: string
    }[]

    for (const { key, url } of uploadedImages) {
      payload[key] = url
    }

    // Step 2: Insert into 'setup' table with new sanitized payload
    const { data, error: insertError } = payload.id
      ? await api().from('setup').update(payload).eq('id', payload.id)
      : await api().from('setup').insert(payload)

    if (insertError) {
      await api()
        .storage.from('setup-images')
        .remove(uploadedFiles.map((f) => f.path))
      throw new Error(insertError.message)
    }

    return data
  } catch (err) {
    if (uploadedFiles.length > 0) {
      await api()
        .storage.from('setup-images')
        .remove(uploadedFiles.map((f) => f.path))
    }
    throw err
  }
}
